import { Tooltip } from '@app/features/analytics/components/tooltip/tooltip';
import React, { useEffect, useState } from 'react';

import { EventType } from './types';

const AVATAR_COLORS = [
  'bg-webinar-bg text-webinar-text',
  'bg-virtual-bg text-virtual-text',
  'bg-hybrid-bg text-hybrid-text',
  'bg-on-site-bg text-on-site-text',
  'bg-lavender-light text-lavender-dark',
];

const getAvatarColor = (email: string): string => {
  const localPart = email.split('@')[0] || email;
  let hash = 5381;

  for (const char of localPart) {
    hash = (hash * 33) ^ char.charCodeAt(0);
  }

  return AVATAR_COLORS[(hash >>> 0) % AVATAR_COLORS.length];
};

const getInitials = (email: string): string => {
  const localPart = email.split('@')[0] || email;
  const parts = localPart.split(/[._-]/).filter(Boolean);

  if (parts.length >= 2) {
    return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
  }

  return localPart
    .replace(/[^a-zA-Z0-9]/g, '')
    .slice(0, 2)
    .toUpperCase();
};

export type TCreatorAvatarData = {
  creatorEmail: EventType['creatorEmail'] | null | undefined;
  pictureUrl?: string | null;
  creatorPictureUrl?: string | null;
};

type CreatorAvatarTemplateProps = {
  data: TCreatorAvatarData;
  // Events DataGrid uses addTooltip=true (default); OrganizerRow passes false
  // because the email is already visible in the adjacent cell.
  addTooltip?: boolean;
};

export const CreatorAvatarTemplate = ({
  data,
  addTooltip = true,
}: CreatorAvatarTemplateProps) => {
  const [pictureFailed, setPictureFailed] = useState(false);
  const email = data.creatorEmail;
  const pictureUrl =
    data.creatorPictureUrl?.trim() || data.pictureUrl?.trim() || undefined;

  useEffect(() => {
    setPictureFailed(false);
  }, [pictureUrl]);

  if (!email) {
    return <span className="text-xxs text-neutral-b2">-</span>;
  }

  const showPicture = Boolean(pictureUrl) && !pictureFailed;

  const avatar = showPicture ? (
    <span
      className="inline-flex h-6 w-6 shrink-0 overflow-hidden rounded-full"
      aria-label={email}
      data-testid="creator-avatar"
    >
      <img
        src={pictureUrl}
        alt=""
        className="block h-full w-full object-cover"
        onError={() => setPictureFailed(true)}
      />
    </span>
  ) : (
    <span
      className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xxs font-medium ${getAvatarColor(
        email,
      )}`}
      aria-label={email}
      data-testid="creator-avatar"
    >
      {getInitials(email)}
    </span>
  );

  if (!addTooltip) {
    return avatar;
  }

  return (
    <Tooltip tooltip={email} placement="bottom-center">
      {avatar}
    </Tooltip>
  );
};
